import { useState } from 'react';
import { Box, Typography, Paper, Grid, Chip, LinearProgress } from '@mui/material';
import { useGetList } from 'react-admin';
import { People, School, Business, Event } from '@mui/icons-material';
import { ListPageHeader } from '@/components/ListPageHeader';
import { InfoBox } from '@/components/help';
import type { TutorRecord, StudentRecord, ClientRecord, SessionRecord } from './types';

interface MetricCardProps {
  label: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  description?: string;
}

const MetricCard = ({ label, value, icon, color, description }: MetricCardProps) => (
  <Paper elevation={0} sx={{ p: 3, textAlign: 'center', borderTop: `4px solid ${color}`, bgcolor: 'action.hover' }}>
    <Box sx={{ color, mb: 1 }}>{icon}</Box>
    <Typography variant="h4" sx={{ fontWeight: 700, color }}>
      {value}
    </Typography>
    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
      {label}
    </Typography>
    {description && (
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, mt: 0.5, display: 'block' }}>
        {description}
      </Typography>
    )}
  </Paper>
);

interface StatusBreakdownProps {
  title: string;
  counts: Record<string, number>;
  colors: Record<string, string>;
}

const StatusBreakdown = ({ title, counts, colors }: StatusBreakdownProps) => (
  <Paper elevation={0} sx={{ p: 2.5, bgcolor: 'action.hover' }}>
    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600 }}>
      {title}
    </Typography>
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      {Object.entries(counts).map(([status, count]) => (
        <Chip
          key={status}
          label={`${status}: ${count}`}
          size="small"
          sx={{ bgcolor: colors[status] ? `${colors[status]}.main` : 'grey.100', color: 'text.primary' }}
        />
      ))}
    </Box>
  </Paper>
);

export const TutoringDashboard = () => {
  const [showOnboarding, setShowOnboarding] = useState(true);

  const { data: tutors, isLoading: tutorsLoading } = useGetList('tutors', { pagination: { page: 1, perPage: 1000 } });
  const { data: students, isLoading: studentsLoading } = useGetList('students', { pagination: { page: 1, perPage: 1000 } });
  const { data: clients, isLoading: clientsLoading } = useGetList('clients', { pagination: { page: 1, perPage: 1000 } });
  const { data: sessions, isLoading: sessionsLoading } = useGetList('tutoring-sessions', { pagination: { page: 1, perPage: 1000 } });

  const isLoading = tutorsLoading || studentsLoading || clientsLoading || sessionsLoading;

  const tutorCounts = {
    Total: tutors?.length ?? 0,
    Active: tutors?.filter((t: TutorRecord) => t.status === 'Active').length ?? 0,
    Inactive: tutors?.filter((t: TutorRecord) => t.status === 'Inactive').length ?? 0,
    Suspended: tutors?.filter((t: TutorRecord) => t.status === 'Suspended').length ?? 0,
  };

  const studentCounts = {
    Total: students?.length ?? 0,
    Active: students?.filter((s: StudentRecord) => s.status === 'Active').length ?? 0,
    Inactive: students?.filter((s: StudentRecord) => s.status === 'Inactive').length ?? 0,
  };

  const clientCounts = {
    Total: clients?.length ?? 0,
    Active: clients?.filter((c: ClientRecord) => c.status === 'Active').length ?? 0,
    Inactive: clients?.filter((c: ClientRecord) => c.status === 'Inactive').length ?? 0,
  };

  const recentSessions = sessions
    ?.sort((a: SessionRecord, b: SessionRecord) => new Date(b.metadata?.createdAt ?? 0).getTime() - new Date(a.metadata?.createdAt ?? 0).getTime())
    .slice(0, 5) ?? [];

  const recentEntities = [
    ...(tutors?.map((t: TutorRecord) => ({ type: 'Tutor', name: 'Tutor', date: t.metadata?.createdAt })) ?? []),
    ...(students?.map((s: StudentRecord) => ({ type: 'Student', name: `${s.legalEntity?.firstName ?? ''} ${s.legalEntity?.lastName ?? ''}`.trim() || 'Student', date: s.metadata?.createdAt })) ?? []),
    ...(clients?.map((c: ClientRecord) => ({ type: 'Client', name: c.name, date: c.metadata?.createdAt })) ?? []),
  ]
    .sort((a, b) => new Date(b.date ?? 0).getTime() - new Date(a.date ?? 0).getTime())
    .slice(0, 5);

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <ListPageHeader title="Tutoring Dashboard" />
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <ListPageHeader title="Tutoring Dashboard" />

      {showOnboarding && (
        <InfoBox
          title="Welcome to Tutoring"
          closable
          onDismiss={() => setShowOnboarding(false)}
        >
          <Typography variant="body2" sx={{ mb: 1 }}>
            Get started with these steps:
          </Typography>
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            <li><Typography variant="body2">Register a tutor with qualifications</Typography></li>
            <li><Typography variant="body2">Add a client organisation</Typography></li>
            <li><Typography variant="body2">Create a tutoring service</Typography></li>
            <li><Typography variant="body2">Set up a tutoring agreement</Typography></li>
          </Box>
        </InfoBox>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <MetricCard label="Tutors" value={tutorCounts.Total} icon={<People />} color="#1976d2" description="Currently delivering sessions" />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <MetricCard label="Students" value={studentCounts.Total} icon={<School />} color="#2e7d32" description="Enrolled in tutoring programs" />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <MetricCard label="Clients" value={clientCounts.Total} icon={<Business />} color="#ed6c02" description="Organisations receiving services" />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <MetricCard label="Sessions" value={sessions?.length ?? 0} icon={<Event />} color="#9c27b0" description="Scheduled and completed" />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <StatusBreakdown
            title="Tutors by Status"
            counts={tutorCounts}
            colors={{ Active: '#2e7d32', Inactive: '#ed6c02', Suspended: '#d32f2f', Draft: '#757575' }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <StatusBreakdown
            title="Students by Status"
            counts={studentCounts}
            colors={{ Active: '#2e7d32', Inactive: '#ed6c02', Draft: '#757575' }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <StatusBreakdown
            title="Clients by Status"
            counts={clientCounts}
            colors={{ Active: '#2e7d32', Inactive: '#ed6c02', Draft: '#757575' }}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={0} sx={{ p: 2.5, bgcolor: 'action.hover' }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600 }}>
              Upcoming Sessions
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Sessions scheduled in the next 7 days
            </Typography>
            {recentSessions.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No recent sessions</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {recentSessions.map((session: SessionRecord) => (
                  <Box key={session.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">{session.id}</Typography>
                    <Chip label={session.status} size="small" />
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={0} sx={{ p: 2.5, bgcolor: 'action.hover' }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600 }}>
              Recent Activity
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Recently created tutors, students, and clients
            </Typography>
            {recentEntities.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No recent activity</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {recentEntities.map((entity, idx) => (
                  <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>{entity.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{entity.type}</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {entity.date ? new Date(entity.date).toLocaleDateString() : '—'}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
